import "./header.css";

import React from "react";
import { Link, useLocation } from "react-router-dom";

import { ARTISTS, NAV_LINKS, ROUTES } from "../../data/content";

//White bar with the logo and a menu button, which opens the full-screen menu (as on Wix, on all screen sizes)
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [openGroup, setOpenGroup] = React.useState(null);
  const { pathname } = useLocation();
  const toggleRef = React.useRef(null);
  const closeRef = React.useRef(null);
  const menuRef = React.useRef(null);

  //Close the menu after navigating
  React.useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  React.useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }
    //Open the artists list the current page is in
    const current = ARTISTS.find((a) => a.path === pathname);
    setOpenGroup(current ? current.group : null);

    //Stop the page behind scrolling, and move focus into the menu
    document.body.style.overflow = "hidden";
    closeRef.current.focus();

    const toggle = toggleRef.current;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        return;
      }
      //Keep Tab inside the menu while it's open
      if (e.key === "Tab") {
        const focusable = [...menuRef.current.querySelectorAll("a, button")].filter((el) => el.offsetParent !== null);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
        else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
      toggle.focus({ preventScroll: true });
    };
  }, [isMenuOpen]);

  const menuLink = (path, label, className) => (
    <Link
      to={path}
      className={`${className} ${pathname === path ? "is-active" : ""}`}
      aria-current={pathname === path ? "page" : undefined}
      onClick={() => setIsMenuOpen(false)}
    >
      {label}
    </Link>
  );

  return (
    <header className={"site-header"}>
      <div className={"site-header-inner"}>
        <Link
          to={ROUTES.HOME}
          className={"site-logo"}
          aria-label={"The Charles Greig Gallery - home"}
        >
          <img
            src={"/images/logo.webp"}
            alt={""}
            width={480}
            height={196}
          />
        </Link>

        <button
          ref={toggleRef}
          type={"button"}
          className={"menu-toggle"}
          aria-expanded={isMenuOpen}
          aria-controls={"site-menu"}
          aria-label={"Open menu"}
          onClick={() => setIsMenuOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <div
        ref={menuRef}
        id={"site-menu"}
        className={"site-menu"}
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Menu"}
        hidden={!isMenuOpen}
      >
        <div className={"site-menu-inner"}>
          <button
            ref={closeRef}
            type={"button"}
            className={"menu-close"}
            aria-label={"Close menu"}
            onClick={() => setIsMenuOpen(false)}
          >
            <svg
              viewBox={"0 0 24 24"}
              aria-hidden={"true"}
            >
              <path d={"M4 4l16 16M20 4L4 20"} />
            </svg>
          </button>
          <nav aria-label={"Main"}>
            <ul className={"site-menu-list"}>
              {NAV_LINKS.map((link) => {
                if (link.path) {
                  return (
                    <li key={link.label}>
                      {menuLink(link.path, link.label, "site-menu-link")}
                    </li>
                  );
                }
                //Artists / New Artists - opens the list of artists
                const isOpen = openGroup === link.group;
                const listId = `menu-${link.group}`;
                return (
                  <li key={link.label}>
                    <button
                      type={"button"}
                      className={`site-menu-link has-sub ${isOpen ? "is-open" : ""}`}
                      aria-expanded={isOpen}
                      aria-controls={listId}
                      onClick={() => setOpenGroup(isOpen ? null : link.group)}
                    >
                      {link.label}
                    </button>
                    <ul
                      id={listId}
                      className={"site-submenu"}
                      hidden={!isOpen}
                    >
                      {ARTISTS.filter((a) => a.group === link.group).map((artist) => (
                        <li key={artist.slug}>
                          {menuLink(artist.path, artist.name, "site-submenu-link")}
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
